/**
 * A BEÁLLÍTÁSOK LAP NYITOTT RÉTEGE — űrlap vagy megerősítő ablak (#25).
 *
 * ⚠️ EGY `Escape` = EGY LÉPÉS, A FÓKUSZTÓL FÜGGETLENÜL. Az első változatban a
 * réteg maga figyelte a leütést (`onKeyDown` + `stopPropagation`). Ez csak addig
 * tartott, amíg a fókusz bent volt: egy mellé kattintás után a fókusz a
 * `<body>`-ra esett, és az `Escape` a teljes lapot zárta — űrlapnál a beírt
 * adattal együtt (code review, 2026-09-11). Ezért a szelet most JELZI, mi van
 * nyitva, és a lap `Escape`-je (`App.tsx`, `settingsShortcuts`) azt zárja előbb.
 */

import { useEffect } from 'react';

/** A szelet így jelzi a lapnak a nyitott rétegét; `null`: nincs nyitva semmi. */
export type LayerRegistrar = (close: (() => void) | null) => void;

export interface SliceLayers {
  /**
   * A szelet épp NEM a rétegét mutatja (betöltés, tiltás, hibás betöltés).
   * ⚠️ Enélkül egy mentés 403-a után az első `Escape` egy láthatatlan űrlapot
   * zárna, és a képernyőn nem történne semmi.
   */
  hidden: boolean;
  confirming: boolean;
  editing: boolean;
  /** Mentés közben az űrlap réteg NEM zárul — de a leütést elnyeli. */
  saving: boolean;
  closeConfirm: () => void;
  closeForm: () => void;
  /**
   * A RÉTEG BEJEGYZÉSE EGY GYEREKKOMPONENSÉ — ez a szelet hozzá sem nyúl (#32).
   *
   * ⚠️ NEM UGYANAZ, MINT A `hidden`. A `hidden` azt mondja: „nincs mit zárni",
   * és `null`-t jegyez be. Az effektusok viszont LENTRŐL FELFELÉ futnak: ha a
   * szelet `null`-t ír, azzal a gyereke bejegyzését TÖRÖLNÉ — és a gyerekben
   * nyitott űrlapon az `Escape` a teljes lapot zárná, a beírt adattal együtt.
   * Ez pontosan az a hiba, amit a fájl fejléce egyszer már leírt, csak egy
   * szinttel feljebb.
   */
  delegated?: boolean;
  /**
   * AMI „EGY LÉPÉS VISSZA", HA NINCS NYITOTT RÉTEG. A szeleteknél ilyen nincs
   * (ott a lap zárul), a cég kapcsolattartói alatt viszont van: a visszaút a
   * céglistára (#32).
   *
   * ⚠️ A `hidden` EZT NEM NYOMJA EL (code review, 2026-09-14). Az első változat
   * elnyomta, és ebből az következett, hogy BETÖLTÉS KÖZBEN — meg egy bukott
   * betöltés után — az `Escape` a teljes Beállítások lapot zárta, miközben a
   * „Vissza a céglistához" gomb ott állt a képernyőn. A `hidden` azt mondja ki,
   * hogy nincs nyitott RÉTEG; a visszaút nem réteg, hanem maga a képernyő.
   */
  fallbackClose?: () => void;
}

/** A mentés alatti `Escape`: nem zár, és a lapot sem engedi bezárni. */
const swallow = () => undefined;

/**
 * Előbb a megerősítő ablak, aztán az űrlap — abban a sorrendben, ahogy
 * egymás fölött állnak. Mentés közben a válasz még úton van: az űrlap nem
 * tűnhet el alóla, és a lap sem.
 */
export function useSettingsLayer(onLayer: LayerRegistrar, layers: SliceLayers): void {
  const {
    hidden,
    confirming,
    editing,
    saving,
    closeConfirm,
    closeForm,
    delegated = false,
    fallbackClose = null,
  } = layers;

  useEffect(() => {
    // ⚠️ KORAI KILÉPÉS, TAKARÍTÁS NÉLKÜL. Egy `return () => onLayer(null)` itt
    // ugyanazt tenné, amit az átadás elkerülni akar: a gyerek bejegyzését
    // törölné a legközelebbi újrafuttatáskor.
    if (delegated) {
      return;
    }

    if (hidden) {
      // Nincs nyitott réteg — de a visszaút, ha van, akkor is egy lépés vissza.
      onLayer(fallbackClose);
    } else if (confirming) {
      onLayer(closeConfirm);
    } else if (editing) {
      onLayer(saving ? swallow : closeForm);
    } else {
      onLayer(fallbackClose);
    }
    return () => onLayer(null);
  }, [
    onLayer,
    hidden,
    confirming,
    editing,
    saving,
    closeConfirm,
    closeForm,
    delegated,
    fallbackClose,
  ]);
}
